import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Users, MessageSquare, ListChecks, Building2, Send, ClipboardList, Activity, AlertTriangle } from "lucide-react";

/**
 * Closed-beta platform-admin summary. Only reachable by owners/admins of an
 * organization marked `internal_workspace=true`. The backend endpoint returns
 * 403 otherwise, so an authorization error surfaces as a friendly gate.
 */
export default function BetaSummary() {
  const { currentOrg } = useAuth();
  const [data, setData] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [gate, setGate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, f, o] = await Promise.all([
          api.get("/platform/beta-summary"),
          api.get("/platform/feedback"),
          api.get("/platform/orgs"),
        ]);
        if (cancelled) return;
        setData(s.data); setFeedback(f.data); setOrgs(o.data);
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 403) setGate("You need platform-admin access to view this page.");
        else setGate("Could not load the beta summary.");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (gate) {
    return (
      <div className="flex-1 flex items-center justify-center p-10" data-testid="beta-summary-gate">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
          <div className="mt-3 text-lg font-semibold">Restricted</div>
          <div className="text-sm text-zinc-600 mt-1">{gate}</div>
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-8 text-sm text-zinc-500">Loading…</div>;

  const {
    orgs: orgsCount, users, invitations, activity, feedback: fbCounts,
  } = data;

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="beta-summary-view">
      <div className="h-14 border-b flex items-center px-6 shrink-0 gap-3">
        <h1 className="text-lg font-semibold">Internal beta summary</h1>
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 border rounded-full px-2 py-0.5">
          {currentOrg?.name} · Platform admin
        </span>
      </div>
      <div className="flex-1 overflow-auto scroll-thin p-6 space-y-8 max-w-6xl">

        <Section title="Reach">
          <StatCard icon={Building2} label="Workspaces" value={orgsCount} />
          <StatCard icon={Users} label="Users total" value={users.total} />
          <StatCard icon={Activity} label="Active in last 7 days" value={users.active_7d} />
          <StatCard icon={Send} label="Waitlist requests" value={fbCounts.waitlist} tone="accent" />
        </Section>

        <Section title="Invitations">
          <StatCard label="Created" value={invitations.created} />
          <StatCard label="Sent (provider accepted)" value={invitations.sent_by_provider} tone="sky" />
          <StatCard label="Failed" value={invitations.failed} tone="red" />
          <StatCard label="Accepted" value={invitations.accepted} tone="emerald" />
        </Section>

        <Section title="Activity">
          <StatCard icon={MessageSquare} label="Channel messages" value={activity.messages - activity.direct_messages} />
          <StatCard icon={MessageSquare} label="Direct messages" value={activity.direct_messages} />
          <StatCard icon={ListChecks} label="Tasks created" value={activity.tasks_created} />
          <StatCard label="Tasks completed" value={activity.tasks_completed} tone="emerald" />
          <StatCard label="Projects" value={activity.projects} />
          <StatCard icon={ClipboardList} label="Requests" value={activity.requests} />
          <StatCard label="Office visits" value={activity.office_visits} />
          <StatCard label="Office layouts" value={activity.office_layouts} />
        </Section>

        {orgs && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">Workspaces</div>
            <div className="border rounded-lg divide-y">
              {orgs.map((o) => (
                <div key={o.id} className="p-3 flex items-center gap-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate flex items-center gap-2">
                      {o.name}
                      {o.internal_workspace && (
                        <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-800">
                          Internal · billing exempt
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      slug={o.slug} · {o.member_count} member{o.member_count === 1 ? "" : "s"}
                      {o.internal_workspace ? ` · limit ${o.internal_member_limit || 50}` : ""}
                    </div>
                  </div>
                  <div className="text-[10px] text-zinc-400">{o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {feedback && (
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">Recent feedback (across every workspace)</div>
            {feedback.length === 0 ? (
              <div className="text-sm text-zinc-500 rounded-lg border p-4">No feedback yet.</div>
            ) : (
              <div className="border rounded-lg divide-y max-h-96 overflow-auto scroll-thin">
                {feedback.slice(0, 40).map((f) => (
                  <div key={f.id} className="p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded ${
                        f.category === "bug" ? "bg-red-100 text-red-700"
                        : f.category === "feature_request" ? "bg-emerald-100 text-emerald-700"
                        : f.category === "confusing" ? "bg-amber-100 text-amber-800"
                        : f.category === "waitlist" ? "bg-orange-100 text-orange-800"
                        : "bg-zinc-100 text-zinc-700"
                      }`}>{f.category}</span>
                      <span className="text-sm font-medium">{f.title}</span>
                      <span className="ml-auto text-[11px] text-zinc-400">{new Date(f.created_at).toLocaleString()}</span>
                    </div>
                    {f.description && <div className="text-sm text-zinc-600 mt-1 line-clamp-3 whitespace-pre-wrap">{f.description}</div>}
                    <div className="text-[11px] text-zinc-400 mt-1 truncate">
                      {f.user_email || "(no email)"} · {f.current_route || "—"} · org {f.org_id || "(waitlist)"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="text-[11px] text-zinc-400 pt-6 border-t">
          Generated {new Date(data.generated_at).toLocaleString()}. This view is restricted to internal-workspace administrators.
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">{title}</div>
      <div className="grid gap-3 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">{children}</div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }) {
  const map = {
    sky: "text-sky-700 bg-sky-50 border-sky-200",
    red: "text-red-700 bg-red-50 border-red-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    accent: "text-orange-800 bg-orange-50 border-orange-200",
  };
  const cls = map[tone] || "text-zinc-800 bg-white border-zinc-200";
  return (
    <div className={`border rounded-lg p-4 ${cls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="text-2xl font-semibold mt-1">{value ?? 0}</div>
    </div>
  );
}
